import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../helpers/constants";
import AdminEndpoint from "./AdminEndpoint";
import RequestLogs from "./RequestLogs";
import { partyTheme } from "../accountDetail/accountDetailTheme";
import AppPageHeader from "../../components/AppPageHeader";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontSize: "12px",
    background: "#fff",
  },
  bodyRow: {
    display: "flex",
    flex: 1,
    minHeight: 0,
  },
  leftPanel: {
    width: "50%",
    padding: "10px 10px 10px 12px",
    borderRight: `1px solid ${partyTheme.chrome.sidebarRule}`,
    overflowY: "auto",
    background: "#fff",
  },
  rightPanel: {
    width: "50%",
    padding: "10px 12px 10px 10px",
    overflowY: "auto",
    background: "#fff",
  },
  section: {
    marginBottom: "14px",
    paddingBottom: "10px",
    borderBottom: `1px solid ${partyTheme.chrome.neutralRule}`,
  },
  inputInline: {
    padding: "3px 6px",
    fontSize: "11px",
    border: `1px solid ${partyTheme.chrome.panelBorder}`,
    marginRight: "4px",
    fontFamily: "monospace",
    backgroundColor: "#fff",
    borderRadius: 0,
  },
  emptyState: {
    padding: "40px 20px",
    color: "#999",
  },
  errorBox: {
    padding: "8px 10px",
    marginTop: "10px",
    backgroundColor: "#fdecec",
    border: "1px solid #e9bcbc",
    color: "#8f1e1e",
    fontSize: "11px",
  },
};

const Section = ({ title, children }) => (
  <div style={styles.section}>
    <h3
      style={{
        margin: "0 0 6px 0",
        fontSize: "11px",
        fontWeight: 600,
        color: "#666",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        lineHeight: 1.2,
      }}
    >
      {title}
    </h3>
    {children}
  </div>
);

function AdminPanel() {
  const [requestLogs, setRequestLogs] = useState([]);
  const [logIdCounter, setLogIdCounter] = useState(0);
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");
  const [expandedEndpoints, setExpandedEndpoints] = useState({});

  // Auth state
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  // Shared state
  const [customerUserId, setCustomerUserId] = useState("");
  const [accountUserId, setAccountUserId] = useState("");
  const [portfolioAccountId, setPortfolioAccountId] = useState("");

  // Account state
  const [accountId, setAccountId] = useState("");
  const [createAccountCustomerId, setCreateAccountCustomerId] = useState("");
  const [createAccountUserId, setCreateAccountUserId] = useState("");
  const [createAccountName, setCreateAccountName] = useState("");

  // Portfolio state
  const [portfolioId, setPortfolioId] = useState("");
  const [portfolioName, setPortfolioName] = useState("");
  const [createPortfolioAccountId, setCreatePortfolioAccountId] = useState("");
  const [createPortfolioName, setCreatePortfolioName] = useState("");

  // Patent state
  const [patentPortfolioId, setPatentPortfolioId] = useState("");
  const [patentId, setPatentId] = useState("");
  const [getPatentId, setGetPatentId] = useState("");
  /** Row `patent_id` from list patents — UUID used in DELETE disassociate path */
  const [portfolioPatentUuid, setPortfolioPatentUuid] = useState("");

  // Detection state
  const [detectionPortfolioId, setDetectionPortfolioId] = useState("");
  const [detectionId, setDetectionId] = useState("");
  const [detectionMaxResults, setDetectionMaxResults] = useState("250");
  const [detectionNextToken, setDetectionNextToken] = useState("");

  useEffect(() => {
    const savedToken = localStorage.getItem("auth_token");
    const savedUser = localStorage.getItem("auth_user");
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(userData);
      } catch {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
      }
    }
  }, []);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2000);
  };

  const makeRequest = async (
    url,
    method = "GET",
    body = null,
    customHeaders = {},
  ) => {
    setLoading(true);
    setError(null);

    const logId = logIdCounter;
    setLogIdCounter((prev) => prev + 1);

    const logEntry = {
      id: logId,
      timestamp: new Date().toISOString(),
      method,
      url,
      headers: customHeaders,
      body,
      status: "pending",
    };

    setRequestLogs((prev) => [logEntry, ...prev]);

    try {
      const options = {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...customHeaders,
        },
      };
      if (body) options.body = JSON.stringify(body);

      const res = await fetch(url, options);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || data || "Request failed");

      setRequestLogs((prev) =>
        prev.map((log) =>
          log.id === logId
            ? {
                ...log,
                status: "success",
                response: data,
                statusCode: res.status,
              }
            : log,
        ),
      );

      showToast("Request successful!");
      return data;
    } catch (err) {
      setRequestLogs((prev) =>
        prev.map((log) =>
          log.id === logId
            ? { ...log, status: "error", error: err.message }
            : log,
        ),
      );

      setError(err.message);
      showToast("Request failed!");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearAllLogs = () => {
    setRequestLogs([]);
    setLogIdCounter(0);
  };

  const dismissLog = (logId) => {
    setRequestLogs((prev) => prev.filter((log) => log.id !== logId));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => showToast("Copied to clipboard!"))
      .catch(() => showToast("Failed to copy"));
  };

  const toggleEndpoint = (id) => {
    setExpandedEndpoints((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExecuteCurl = async (curlCommand) => {
    try {
      // Parse method
      const methodMatch = curlCommand.match(/curl\s+-X\s+(\w+)/i);
      const method = methodMatch ? methodMatch[1].toUpperCase() : "GET";

      // Parse URL - handle both quoted and unquoted
      const urlMatch = curlCommand.match(/["']?(https?:\/\/[^\s"']+)["']?/);
      if (!urlMatch) {
        setError("Could not parse URL from curl command");
        return;
      }
      const url = urlMatch[1];

      // Parse headers
      const headerRegex = /-H\s+["']([^:]+):\s*([^"']+)["']/g;
      const headers = {};
      let headerMatch;
      while ((headerMatch = headerRegex.exec(curlCommand)) !== null) {
        headers[headerMatch[1].trim()] = headerMatch[2].trim();
      }

      // Parse body
      let body = null;
      const bodyMatch = curlCommand.match(/-d\s+['"](.+?)['"]\s*$/s);
      if (bodyMatch) {
        try {
          body = JSON.parse(bodyMatch[1]);
        } catch (e) {
          // If JSON parse fails, send as string
          body = bodyMatch[1];
        }
      }

      await makeRequest(url, method, body, headers);
    } catch (err) {
      setError("Failed to execute curl command: " + err.message);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) return setError("Please enter email and password");
    try {
      const data = await makeRequest(`${API_BASE_URL}/login`, "POST", {
        email,
        password,
      });
      if (data && data.token) {
        const userData = {
          user_id: data.user_id,
          email: data.email,
          display_name: data.display_name,
        };
        setToken(data.token);
        setUser(userData);
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("auth_user", JSON.stringify(userData));
        setEmail("");
        setPassword("");
        showToast("Login successful!");
      }
    } catch {}
  };

  const handleRegister = async () => {
    if (!email || !password || !displayName)
      return setError("Please fill in all fields");
    try {
      await makeRequest(`${API_BASE_URL}/register`, "POST", {
        email,
        password,
        display_name: displayName,
      });
      setEmail("");
      setPassword("");
      setDisplayName("");
      setAuthMode("login");
    } catch {}
  };

  const handleLogout = () => {
    setToken("");
    setUser(null);
    setError(null);
    setEmail("");
    setPassword("");
    setDisplayName("");
    setCustomerUserId("");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    showToast("Logged out successfully");
  };

  // Endpoint handlers
  const handleHealthCheck = () => makeRequest(`${API_BASE_URL}/health`);
  const handleDbHealthCheck = () => makeRequest(`${API_BASE_URL}/health/db`);

  const handleGetCustomers = () => {
    if (!customerUserId) return setError("Please enter a user ID");
    makeRequest(`${API_BASE_URL}/v1/customer`, "GET", null, {
      "X-User-Id": customerUserId,
    });
  };

  const handleGetAccounts = () => {
    if (!accountUserId) return setError("Please enter user ID");
    makeRequest(`${API_BASE_URL}/v1/account`, "GET", null, {
      "X-User-Id": accountUserId,
    });
  };

  const handleGetAccount = () => {
    if (!accountId || !accountUserId)
      return setError("Please enter account ID and user ID");
    makeRequest(`${API_BASE_URL}/v1/account/${accountId}`, "GET", null, {
      "X-User-Id": accountUserId,
    });
  };

  const handleCreateAccount = () => {
    if (!createAccountCustomerId || !createAccountUserId || !createAccountName)
      return setError("Please enter customer ID, user ID, and account name");
    makeRequest(`${API_BASE_URL}/v1/account`, "POST", {
      customer_id: createAccountCustomerId,
      user_id: createAccountUserId,
      account_name: createAccountName,
    });
  };

  const handleGetPortfolios = () =>
    makeRequest(`${API_BASE_URL}/v1/portfolio`, "GET", null, {
      "X-Account-Id": portfolioAccountId,
    });

  const handleGetPortfolio = () => {
    if (!portfolioId) return setError("Please enter a portfolio ID");
    makeRequest(`${API_BASE_URL}/v1/portfolio/${portfolioId}`, "GET", null, {
      "X-Account-Id": portfolioAccountId,
    });
  };

  const handleUpdatePortfolio = () => {
    if (!portfolioId || !portfolioName)
      return setError("Please enter a portfolio ID and name");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${portfolioId}`,
      "PUT",
      { portfolio_name: portfolioName },
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const handleCreatePortfolio = () => {
    if (!createPortfolioAccountId || !createPortfolioName)
      return setError("Please enter account ID and portfolio name");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio`,
      "POST",
      { portfolio_name: createPortfolioName },
      { "X-Account-Id": createPortfolioAccountId },
    );
  };

  const handleGetPortfolioPatents = () => {
    if (!patentPortfolioId) return setError("Please enter a portfolio ID");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${patentPortfolioId}/patent`,
      "GET",
      null,
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const handleAddPatentToPortfolio = () => {
    if (!patentPortfolioId || !patentId)
      return setError("Please enter portfolio ID and patent ID");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${patentPortfolioId}/patent`,
      "POST",
      { application_number: patentId },
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const handleGetPatent = () => {
    if (!patentPortfolioId || !getPatentId)
      return setError("Please enter portfolio ID and patent ID");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${patentPortfolioId}/patent/${getPatentId}`,
      "GET",
      null,
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const handleDisassociatePatentFromPortfolio = () => {
    if (!patentPortfolioId || !portfolioPatentUuid)
      return setError("Please enter portfolio ID and patent UUID");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${patentPortfolioId}/patent/${portfolioPatentUuid}`,
      "DELETE",
      null,
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const handleGetDetections = () => {
    if (!detectionPortfolioId) return setError("Please enter a portfolio ID");
    const queryParams = new URLSearchParams();
    if (detectionMaxResults)
      queryParams.append("maxResults", detectionMaxResults);
    if (detectionNextToken) queryParams.append("nextToken", detectionNextToken);
    const url = `${API_BASE_URL}/v1/portfolio/${detectionPortfolioId}/detection${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    makeRequest(url, "GET", null, { "X-Account-Id": portfolioAccountId });
  };

  const handleGetDetectionDetails = () => {
    if (!detectionPortfolioId || !detectionId)
      return setError("Please enter portfolio ID and detection ID");
    makeRequest(
      `${API_BASE_URL}/v1/portfolio/${detectionPortfolioId}/detection/${detectionId}`,
      "GET",
      null,
      { "X-Account-Id": portfolioAccountId },
    );
  };

  const renderAuthSection = () => {
    if (user) return null;
    return (
    <div style={styles.section}>
      <h3 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>Authentication</h3>
        <div>
          <div style={{ marginBottom: "8px" }}>
            <button
              onClick={() => setAuthMode("login")}
              style={{
                padding: "4px 8px",
                fontSize: "12px",
                marginRight: "5px",
                fontWeight: authMode === "login" ? "bold" : "normal",
              }}
            >
              Login Form
            </button>
            <button
              onClick={() => setAuthMode("register")}
              style={{
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: authMode === "register" ? "bold" : "normal",
              }}
            >
              Register Form
            </button>
          </div>
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ ...styles.inputInline, width: "200px" }}
            />
          </div>
          <div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                ...styles.inputInline,
                width: "200px",
                marginTop: "4px",
              }}
            />
          </div>
          {authMode === "register" && (
            <div>
              <input
                type="text"
                placeholder="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={{
                  ...styles.inputInline,
                  width: "200px",
                  marginTop: "4px",
                }}
              />
            </div>
          )}
          <div style={{ marginTop: "8px" }}>
            <button
              onClick={authMode === "login" ? handleLogin : handleRegister}
              style={{ padding: "4px 8px", fontSize: "12px" }}
            >
              {authMode === "login" ? "Login" : "Register"}
            </button>
          </div>
        </div>
    </div>
    );
  };

  return (
    <div style={styles.root}>
      <AppPageHeader
        title="Admin Tool"
        metaLine={user ? undefined : "dehisce.com"}
        user={user || undefined}
        onLogout={user ? handleLogout : undefined}
        subtitle={
          user ? (
            <>
              user_id: {user.user_id}{" "}
              <button
                type="button"
                onClick={() => copyToClipboard(user.user_id)}
                style={{
                  marginLeft: "6px",
                  padding: "2px 6px",
                  fontSize: "11px",
                  cursor: "pointer",
                  border: "1px solid #d0d0d0",
                  borderRadius: 0,
                  background: "#fff",
                }}
              >
                copy
              </button>
            </>
          ) : undefined
        }
      />

      <div style={styles.bodyRow}>
      <div style={styles.leftPanel}>
        {renderAuthSection()}

        <Section title="Health Checks">
          <AdminEndpoint
            id="health"
            method="GET"
            path="/health"
            description="Check if the API server is running and responding"
            isExpanded={expandedEndpoints["health"]}
            onToggle={toggleEndpoint}
            onExecute={handleHealthCheck}
            inputs={[]}
            headers={{}}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="health-db"
            method="GET"
            path="/health/db"
            description="Check database connectivity and status"
            isExpanded={expandedEndpoints["health-db"]}
            onToggle={toggleEndpoint}
            onExecute={handleDbHealthCheck}
            inputs={[]}
            headers={{}}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
        </Section>

        <Section title="Customer Operations">
          <AdminEndpoint
            id="customer-get"
            method="GET"
            path="/v1/customer"
            description="Retrieve customer information for the authenticated user"
            isExpanded={expandedEndpoints["customer-get"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetCustomers}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-User-Id",
                value: customerUserId,
                onChange: (e) => setCustomerUserId(e.target.value),
              },
            ]}
            headers={{ "X-User-Id": customerUserId }}
          />
        </Section>

        <Section title="Account Operations">
          <AdminEndpoint
            id="account-list"
            method="GET"
            path="/v1/account"
            description="List all accounts for a user"
            isExpanded={expandedEndpoints["account-list"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetAccounts}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-User-Id",
                value: accountUserId,
                onChange: (e) => setAccountUserId(e.target.value),
              },
            ]}
            headers={{ "X-User-Id": accountUserId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="account-get"
            method="GET"
            path="/v1/account/:id"
            description="Retrieve a specific account by ID"
            isExpanded={expandedEndpoints["account-get"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetAccount}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-User-Id",
                value: accountUserId,
                onChange: (e) => setAccountUserId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "account_id",
                value: accountId,
                onChange: (e) => setAccountId(e.target.value),
              },
            ]}
            headers={{ "X-User-Id": accountUserId }}
            urlParams={{ id: accountId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="account-create"
            method="POST"
            path="/v1/account"
            description="Create a new account for a customer"
            isExpanded={expandedEndpoints["account-create"]}
            onToggle={toggleEndpoint}
            onExecute={handleCreateAccount}
            inputs={[
              {
                label: "Body",
                placeholder: "customer_id",
                value: createAccountCustomerId,
                onChange: (e) => setCreateAccountCustomerId(e.target.value),
                style: { marginBottom: "4px" },
              },
              {
                placeholder: "user_id",
                value: createAccountUserId,
                onChange: (e) => setCreateAccountUserId(e.target.value),
                style: { marginBottom: "4px" },
              },
              {
                placeholder: "account_name",
                value: createAccountName,
                onChange: (e) => setCreateAccountName(e.target.value),
              },
            ]}
            headers={{ "Content-Type": "application/json" }}
            body={{
              customer_id: createAccountCustomerId,
              user_id: createAccountUserId,
              account_name: createAccountName,
            }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
        </Section>

        <Section title="Portfolio Operations">
          <AdminEndpoint
            id="portfolio-list"
            method="GET"
            path="/v1/portfolio"
            description="List all portfolios for an account"
            isExpanded={expandedEndpoints["portfolio-list"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetPortfolios}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
            ]}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
            headers={{ "X-Account-Id": portfolioAccountId }}
          />
          <AdminEndpoint
            id="portfolio-get"
            method="GET"
            path="/v1/portfolio/:id"
            description="Retrieve a specific portfolio by ID"
            isExpanded={expandedEndpoints["portfolio-get"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetPortfolio}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: portfolioId,
                onChange: (e) => setPortfolioId(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{ id: portfolioId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="portfolio-update"
            method="PUT"
            path="/v1/portfolio/:id"
            description="Update an existing portfolio's name"
            isExpanded={expandedEndpoints["portfolio-update"]}
            onToggle={toggleEndpoint}
            onExecute={handleUpdatePortfolio}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: portfolioId,
                onChange: (e) => setPortfolioId(e.target.value),
              },
              {
                label: "Body",
                placeholder: "portfolio_name",
                value: portfolioName,
                onChange: (e) => setPortfolioName(e.target.value),
              },
            ]}
            headers={{
              "X-Account-Id": portfolioAccountId,
              "Content-Type": "application/json",
            }}
            urlParams={{ id: portfolioId }}
            body={{ portfolio_name: portfolioName }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="portfolio-create"
            method="POST"
            path="/v1/portfolio"
            description="Create a new portfolio for an account"
            isExpanded={expandedEndpoints["portfolio-create"]}
            onToggle={toggleEndpoint}
            onExecute={handleCreatePortfolio}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: createPortfolioAccountId,
                onChange: (e) => setCreatePortfolioAccountId(e.target.value),
              },
              {
                label: "Body",
                placeholder: "portfolio_name",
                value: createPortfolioName,
                onChange: (e) => setCreatePortfolioName(e.target.value),
              },
            ]}
            headers={{
              "X-Account-Id": createPortfolioAccountId,
              "Content-Type": "application/json",
            }}
            body={{ portfolio_name: createPortfolioName }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
        </Section>

        <Section title="Patent Operations">
          <AdminEndpoint
            id="patent-list"
            method="GET"
            path="/v1/portfolio/:id/patent"
            description="List all patents in a portfolio"
            isExpanded={expandedEndpoints["patent-list"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetPortfolioPatents}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: patentPortfolioId,
                onChange: (e) => setPatentPortfolioId(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{ id: patentPortfolioId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="patent-get"
            method="GET"
            path="/v1/portfolio/:id/patent/:patentId"
            description="Get a patent's claims and claim numbers for a portfolio"
            isExpanded={expandedEndpoints["patent-get"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetPatent}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: patentPortfolioId,
                onChange: (e) => setPatentPortfolioId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "patent_id",
                value: getPatentId,
                onChange: (e) => setGetPatentId(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{ id: patentPortfolioId, patentId: getPatentId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="patent-add"
            method="POST"
            path="/v1/portfolio/:id/patent"
            description="Add a patent to a portfolio, this adds to the associate worker api"
            isExpanded={expandedEndpoints["patent-add"]}
            onToggle={toggleEndpoint}
            onExecute={handleAddPatentToPortfolio}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: patentPortfolioId,
                onChange: (e) => setPatentPortfolioId(e.target.value),
              },
              {
                label: "Body",
                placeholder: "application_number (e.g., 17123456)",
                value: patentId,
                onChange: (e) => setPatentId(e.target.value),
              },
            ]}
            headers={{
              "X-Account-Id": portfolioAccountId,
              "Content-Type": "application/json",
            }}
            urlParams={{ id: patentPortfolioId }}
            body={{ patent_id: patentId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="patent-disassociate"
            method="DELETE"
            path="/v1/portfolio/:id/patent/:patentUuid"
            description="Remove a patent from a portfolio (disassociate). Use the patent UUID from the list patents response (patent_id field)."
            isExpanded={expandedEndpoints["patent-disassociate"]}
            onToggle={toggleEndpoint}
            onExecute={handleDisassociatePatentFromPortfolio}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: patentPortfolioId,
                onChange: (e) => setPatentPortfolioId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "patent_uuid (list patents)",
                value: portfolioPatentUuid,
                onChange: (e) => setPortfolioPatentUuid(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{
              id: patentPortfolioId,
              patentUuid: portfolioPatentUuid,
            }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
        </Section>

        <Section title="Detection Operations">
          <AdminEndpoint
            id="detection-list"
            method="GET"
            path="/v1/portfolio/:id/detection"
            description="List all detections (claim overlaps) for a portfolio"
            isExpanded={expandedEndpoints["detection-list"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetDetections}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: detectionPortfolioId,
                onChange: (e) => setDetectionPortfolioId(e.target.value),
              },
              {
                label: "Query Parameters (optional)",
                placeholder: "maxResults (default: 250)",
                value: detectionMaxResults,
                onChange: (e) => setDetectionMaxResults(e.target.value),
                style: { marginBottom: "4px" },
              },
              {
                placeholder: "nextToken (for pagination)",
                value: detectionNextToken,
                onChange: (e) => setDetectionNextToken(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{ id: detectionPortfolioId }}
            copyToClipboard={copyToClipboard}
            onExecuteCurl={handleExecuteCurl}
          />
          <AdminEndpoint
            id="detection-details"
            method="GET"
            path="/v1/portfolio/:id/detection/:detection_id"
            description="Get detailed claim comparisons for a specific detection"
            isExpanded={expandedEndpoints["detection-details"]}
            onToggle={toggleEndpoint}
            onExecute={handleGetDetectionDetails}
            inputs={[
              {
                label: "Headers",
                placeholder: "X-Account-Id",
                value: portfolioAccountId,
                onChange: (e) => setPortfolioAccountId(e.target.value),
              },
              {
                label: "Parameters",
                placeholder: "portfolio_id",
                value: detectionPortfolioId,
                onChange: (e) => setDetectionPortfolioId(e.target.value),
                style: { marginBottom: "4px" },
              },
              {
                placeholder: "detection_id",
                value: detectionId,
                onChange: (e) => setDetectionId(e.target.value),
              },
            ]}
            headers={{ "X-Account-Id": portfolioAccountId }}
            urlParams={{ id: detectionPortfolioId, detection_id: detectionId }}
          />
        </Section>
      </div>

      {/* Right Panel - Request Logs */}
      <RequestLogs
        requestLogs={requestLogs}
        loading={loading}
        error={error}
        onClearAll={clearAllLogs}
        onDismissLog={dismissLog}
        copyToClipboard={copyToClipboard}
      />
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: "#333",
            color: "#fff",
            padding: "8px 12px",
            fontSize: "11px",
            border: "1px solid #000",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
